import React from "react";
import ReactDOM from "react-dom";
import "../../Styles/Modal.scss";
import { CSSTransition } from "react-transition-group";
import { cx, css } from "emotion";
import { CLASSNAME_PREFIX } from "./classname";

type Props = {
  isOpen: boolean;
  onToggle: () => void;
  width?: string;
  height?: string;
  transitionTimeout:
    | number
    | {
        appear?: number | undefined;
        enter?: number | undefined;
        exit?: number | undefined;
      };
  parent: Element;
  overlayClassName?: string;
  contentClassName?: string;
  contentContainerClassName?: string;
  staticBackdrop?: boolean;
};

type State = {
  isContentOpen: boolean;
  isOverlayOpen: boolean;
  isMouseClickedOverModalContent: boolean;
  wasExternalToggle: boolean;
};

export class Modal extends React.Component<Props, State> {
  static defaultProps = {
    transitionTimeout: 200,
    parent: document.body,
    staticBackdrop: false
  };

  constructor(props: Props) {
    super(props);

    this.state = {
      isContentOpen: false,
      isOverlayOpen: props.isOpen,
      isMouseClickedOverModalContent: false,
      wasExternalToggle: false
    };
  }

  componentDidUpdate = (prevProps: Props) => {
    if (this.props.isOpen !== prevProps.isOpen) {
      if (this.state.isOverlayOpen) {
        this.setState({ wasExternalToggle: true }, () => this.onContentToggle());
      } else {
        if (this.state.wasExternalToggle) {
          this.setState({ wasExternalToggle: false }, () => this.props.onToggle());
          return;
        }

        this.setState({ isOverlayOpen: this.props.isOpen });
      }
    }
  };

  onContentToggle = () => this.setState(prevState => ({ isContentOpen: !prevState.isContentOpen }));

  onOverlayToggle = () => {
    this.setState(prevState => ({ isOverlayOpen: !prevState.isOverlayOpen }));
  };

  mouseDownOverContent = () => {
    this.setState(prevState => ({ isMouseClickedOverModalContent: true }));
  };

  mouseUpOverContent = () => {
    this.setState(prevState => ({ isMouseClickedOverModalContent: false }));
  };

  onClickOverlay = () => {
    if (this.state.isMouseClickedOverModalContent) {
      // block this and just disable the mouse clicked over modal content
      this.setState(prevState => ({ isMouseClickedOverModalContent: false }));
    } else if (!this.props.staticBackdrop && this.props.isOpen) {
      this.setState(
        prevState => ({ isMouseClickedOverModalContent: false }),
        () => {
          this.props.onToggle();
        }
      );
    }
  };

  render() {
    return ReactDOM.createPortal(
      <CSSTransition
        in={this.state.isOverlayOpen}
        timeout={this.props.transitionTimeout}
        onEntered={this.onContentToggle}
        onExited={this.props.onToggle}
        classNames={`${CLASSNAME_PREFIX}overlay-transition`}
        unmountOnExit
      >
        <div
          className={cx(`${CLASSNAME_PREFIX}overlay`, this.props.overlayClassName)}
          onClick={this.onClickOverlay}
        >
          <CSSTransition
            in={this.state.isContentOpen}
            timeout={150}
            classNames={`${CLASSNAME_PREFIX}content-transition`}
            unmountOnExit
            onExited={this.onOverlayToggle}
            onClick={(e: any) => e.stopPropagation()}
          >
            <div
              className={cx(
                `${CLASSNAME_PREFIX}content`,
                this.props.contentClassName,
                css`
                  ${this.props.width ? `width: ${this.props.width};` : ""}
                  ${this.props.height ? `height: ${this.props.height};` : ""}
                `
              )}
              onClick={e => e.stopPropagation()}
              onMouseDown={this.mouseDownOverContent}
              onMouseUp={this.mouseUpOverContent}
            >
              <div
                className={cx(
                  `${CLASSNAME_PREFIX}content-container`,
                  this.props.contentContainerClassName
                )}
              >
                {this.props.children}
              </div>
            </div>
          </CSSTransition>
        </div>
      </CSSTransition>,
      this.props.parent
    );
  }
}
